/**
 * ChatContext — stato globale della chat WhatsApp-style.
 * - Apre UNA connessione SSE per l'utente loggato (qualsiasi ruolo).
 * - Mantiene il conteggio non letti per il badge in navbar.
 * - Espone open/setOpen del pannello flottante + subscribe agli eventi realtime.
 * - Notifica di sistema + beep (Web Audio) sui messaggi in arrivo quando il
 *   pannello è chiuso o non si sta guardando quella conversazione.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useChatStream } from "../hooks/useChatStream";

const Ctx = createContext(null);

export function ChatProvider({ user, children }) {
  const enabled = !!user;
  const isAdmin = user?.role === "admin";
  const [open, setOpenState] = useState(false);
  const [unread, setUnread] = useState(0);
  const listenersRef = useRef(new Set());
  const activeConvRef = useRef(null);
  const openRef = useRef(false);
  const audioCtxRef = useRef(null);
  useEffect(() => { openRef.current = open; }, [open]);

  const ensureAudio = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        const Ctor = window.AudioContext || window.webkitAudioContext;
        if (Ctor) audioCtxRef.current = new Ctor();
      }
      if (audioCtxRef.current?.state === "suspended") audioCtxRef.current.resume();
    } catch { /* ignore */ }
  }, []);

  const playBeep = useCallback(() => {
    try {
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = 880;
      g.gain.setValueAtTime(0.06, ctx.currentTime);
      o.connect(g); g.connect(ctx.destination);
      o.start();
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
      o.stop(ctx.currentTime + 0.27);
    } catch { /* ignore */ }
  }, []);

  const setOpen = useCallback((v) => {
    setOpenState(v);
    if (v) {
      ensureAudio();
      if ("Notification" in window && Notification.permission === "default") {
        try { Notification.requestPermission(); } catch { /* ignore */ }
      }
    }
  }, [ensureAudio]);

  const setActiveConversation = useCallback((id) => { activeConvRef.current = id; }, []);

  const refreshUnread = useCallback(async () => {
    if (!enabled) { setUnread(0); return; }
    try {
      const { data } = await api.get("/chat/unread");
      setUnread(data?.count || 0);
    } catch { /* silent */ }
  }, [enabled]);

  const dispatch = useCallback((ev) => {
    for (const cb of listenersRef.current) { try { cb(ev); } catch { /* ignore */ } }
  }, []);

  const maybeNotify = useCallback((msg) => {
    if (!msg) return;
    const incoming = msg.sender_role === (isAdmin ? "user" : "admin");
    if (!incoming) return;
    const viewing = openRef.current && activeConvRef.current === msg.conversation_id;
    if (viewing) return;
    playBeep();
    if ("Notification" in window && Notification.permission === "granted") {
      const body = msg.body || (msg.attachments?.length ? "Allegato" : "Nuovo messaggio");
      try {
        const n = new Notification(msg.sender_name || "Nuovo messaggio", {
          body,
          tag: `chat-${msg.conversation_id}`,
          icon: "/favicon.ico",
        });
        n.onclick = () => { window.focus(); setOpen(true); n.close(); };
      } catch { /* ignore */ }
    }
  }, [isAdmin, playBeep, setOpen]);

  useChatStream({
    enabled,
    onEvent: (ev) => {
      dispatch(ev);
      refreshUnread();
      if (ev?.type === "chat.message") maybeNotify(ev.message);
    },
  });

  useEffect(() => { refreshUnread(); }, [refreshUnread]);
  useEffect(() => {
    if (!enabled) return undefined;
    const onFocus = () => refreshUnread();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [enabled, refreshUnread]);

  const subscribe = useCallback((cb) => {
    listenersRef.current.add(cb);
    return () => listenersRef.current.delete(cb);
  }, []);

  const value = { open, setOpen, unread, refreshUnread, subscribe, setActiveConversation, user };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useChat() {
  const v = useContext(Ctx);
  if (!v) return { open: false, setOpen: () => {}, unread: 0, refreshUnread: () => {}, subscribe: () => () => {}, setActiveConversation: () => {}, user: null };
  return v;
}
